import React from "react";
import { chatMessengerShow } from "@origin-digital/event-dispatcher";
import {
  Box,
  Button,
  Card,
  Column,
  Columns,
  Heading,
  Stack,
  StackSection,
  Strong,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import { IconPhone } from "@origin-digital/ods-icons";
import { alert } from "@origin-digital/reporting-client";
import styled from "styled-components";
import {
  IBottleDeliverability,
  IDeliverabilityStatus,
} from "../../model/IDeliverability";
import { formatPhone, sanitisePhone } from "../../utils/formatter";
import {
  shouldSendOfferTabAnalytics,
  formatOfferTabForAnalytics,
  IOfferTab,
  IOfferTabAnalytics,
  OfferTabAnalytics,
  OfferType,
} from "../Offers/offerTab";
import content from "./content.json";
import { APP_NAME } from "../../consts";

const InlineChatLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${(props) => props.theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
`;

export const AuthDealerScreen = ({
  deliverability,
  postcode,
  type,
  offerTab,
  analyticsSent,
  setAnalyticsSent,
}: {
  deliverability?: IBottleDeliverability;
  postcode?: string;
  type?: OfferType;
  offerTab?: IOfferTab;
  analyticsSent?: IOfferTabAnalytics;
  setAnalyticsSent?: (key: IOfferTabAnalytics) => void;
}) => {
  const dealer = deliverability?.alternateDeliveryDetails;
  const postcodePrefix =
    type === OfferType.EXCHANGE
      ? content.postcodePrefixSwap
      : content.postcodePrefixRefill;
  const heading =
    deliverability?.status === IDeliverabilityStatus.DELIVER_SUPPLIER
      ? content.localDealer
      : content.lpgDealer;

  if (
    offerTab &&
    setAnalyticsSent &&
    shouldSendOfferTabAnalytics(offerTab, analyticsSent)
  ) {
    setAnalyticsSent(OfferTabAnalytics(offerTab, analyticsSent));
    alert.base("notification", {
      appName: APP_NAME,
      alertMessage: heading,
      alertCode: formatOfferTabForAnalytics(offerTab),
    });
  }

  return (
    <StackSection width="large" data-id="auth-dealer-screen">
      <Card padding="medium">
        <Stack space="xlarge">
          {/* Header */}
          <Columns direction={{ xs: "column", lg: "row-reverse" }}>
            <Column width="content">
              <Box
                display="flex"
                marginBottom={{ xs: "large", lg: "none" }}
                marginLeft={{ xs: "none", lg: "large" }}
              >
                <img
                  src={content.imageUrl}
                  width="96"
                  height="96"
                  alt={content.imageAlt}
                  data-id="auth-dealer-screen-image"
                />
              </Box>
            </Column>
            <Column>
              <Stack space="xsmall">
                <Text
                  variant="subtitle"
                  data-id="auth-dealer-screen-postcode-text"
                >
                  {postcodePrefix} <Strong>{postcode}</Strong>
                </Text>
                <Heading variant="h2" data-id="auth-dealer-screen-title">
                  {heading}
                </Heading>
              </Stack>
            </Column>
          </Columns>

          {/* Auth Dealer */}
          {dealer && (
            <Box data-id="auth-dealer-screen-dealer-section">
              <Card
                backgroundColor="grey50"
                data-id="auth-dealer-screen-dealer-card"
              >
                <Stack space="large">
                  <Stack space="small">
                    {dealer.name && (
                      <Heading
                        variant="h3"
                        weight="regular"
                        tone="neutralDark"
                        data-id="auth-dealer-screen-dealer-name"
                      >
                        {dealer.name}
                      </Heading>
                    )}
                    <Text data-id="auth-dealer-screen-dealer-desc">
                      {content.dealerDescription}
                    </Text>
                  </Stack>
                  {dealer.phone && (
                    <Button
                      data-id="auth-dealer-screen-dealer-phone-btn"
                      component="a"
                      href={`tel:${sanitisePhone(dealer.phone)}`}
                      icon={<IconPhone color="neutralDark" />}
                      iconPosition="leading"
                    >
                      {formatPhone(dealer.phone)}
                    </Button>
                  )}
                </Stack>
              </Card>
            </Box>
          )}

          {/* Get in touch */}
          <Box data-id="auth-dealer-screen-footer">
            <Card padding="small" backgroundColor="grey50">
              <Text variant="body-small">
                {content.footerPrefix}{" "}
                <InlineChatLink
                  data-id="auth-dealer-screen-chat-link"
                  onClick={() => chatMessengerShow({})}
                >
                  {content.footerChatLinkText}
                </InlineChatLink>{" "}
                {content.footerCallPrefix}{" "}
                <TextLink
                  data-id="auth-dealer-screen-footer-phone-link"
                  href={content.footerPhoneHref}
                >
                  {content.footerPhone}
                </TextLink>
                .
              </Text>
            </Card>
          </Box>
        </Stack>
      </Card>
    </StackSection>
  );
};
